import React, { useState, useEffect } from 'react';
import Logo from './Logo';
import apiClient from '../utils/apiClient';
import { API_ENDPOINTS } from '../config/api';
import SecurityPage from './SecurityPage';

const AccountPage = ({ onBack, activeBottomTab, setActiveBottomTab, userData: userDataProp }) => {
  const [userData, setUserData] = useState({
    full_name: userDataProp?.full_name || 'Loading...',
    email: userDataProp?.email || '',
    phone_no: '',
    alamat: '',
    birth_date: '',
    id: userDataProp?.id || null
  });
  const [stats, setStats] = useState({
    totalInvestasi: 0,
    totalTabunganAktif: 0,
    loading: true
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState({
    full_name: '',
    email: '',
    phone_no: '',
    alamat: '',
    birth_date: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordFormData, setPasswordFormData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [showSecurityPage, setShowSecurityPage] = useState(false);

  // Fetch user data jika belum ada atau perlu refresh
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        let userId = userDataProp?.id;
        
        // Jika tidak ada user_id dari prop, coba ambil dari localStorage
        if (!userId) {
          const savedUserData = localStorage.getItem('user_data');
          if (savedUserData) {
            const parsed = JSON.parse(savedUserData);
            userId = parsed.id;
          }
        }

        if (userId) {
          const response = await apiClient.get(API_ENDPOINTS.USER.GET_BY_ID(userId));
          
          // Handle berbagai format response
          let userFromApi = null;
          if (response.data && response.data.user) {
            userFromApi = response.data.user;
          } else if (response.data && response.data.data) {
            userFromApi = response.data.data;
          } else if (response.data && !response.data.user) {
            userFromApi = response.data;
          } else if (!response.data) {
            userFromApi = response;
          }
          
          if (userFromApi) {
            setUserData({
              full_name: userFromApi.full_name || 'User',
              email: userFromApi.email || '',
              phone_no: userFromApi.phone_no || '',
              alamat: userFromApi.alamat || '',
              birth_date: userFromApi.birth_date || '',
              id: userFromApi.id
            });
          }
        } else {
          // Coba fetch dari /api/users/me
          const meResponse = await apiClient.get(API_ENDPOINTS.USER.GET_ME);
          if (meResponse.data && meResponse.data.data) {
            const userMe = meResponse.data.data;
            setUserData({
              full_name: userMe.full_name || 'User',
              email: userMe.email || '',
              phone_no: userMe.phone_no || '',
              alamat: userMe.alamat || '',
              birth_date: userMe.birth_date || '',
              id: userMe.id
            });
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        // Jika ada data dari prop, gunakan itu
        if (userDataProp) {
          setUserData({
            full_name: userDataProp.full_name || 'User',
            email: userDataProp.email || '',
            phone_no: '',
            alamat: '',
            birth_date: '',
            id: userDataProp.id
          });
        }
      }
    };

    // Jika userDataProp tidak ada atau tidak lengkap, fetch dari API
    if (!userDataProp || !userDataProp.full_name || userDataProp.full_name === 'Loading...') {
      fetchUserData();
    } else {
      // Gunakan data dari prop
      setUserData({
        full_name: userDataProp.full_name,
        email: userDataProp.email || '',
        phone_no: '',
        alamat: '',
        birth_date: '',
        id: userDataProp.id
      });
      fetchUserData(); // Tetap fetch untuk mendapatkan data lengkap
    }
  }, [userDataProp]);

  // Fetch investasi dan tabungan stats
  useEffect(() => {
    const fetchStats = async () => {
      try {
        let userId = userData.id;
        
        // Jika tidak ada user_id, coba ambil dari localStorage
        if (!userId) {
          const savedUserData = localStorage.getItem('user_data');
          if (savedUserData) {
            const parsed = JSON.parse(savedUserData);
            userId = parsed.id;
          }
        }

        if (!userId) {
          setStats({
            totalInvestasi: 0,
            totalTabunganAktif: 0,
            loading: false
          });
          return;
        }

        setStats(prev => ({ ...prev, loading: true }));

        // Build query parameters untuk investasi dan tabungan
        const investasiParams = new URLSearchParams({ user_id: userId });
        const tabunganParams = new URLSearchParams({ user_id: userId });

        // Fetch investasi dan tabungan secara parallel
        const [investasiResponse, tabunganResponse] = await Promise.all([
          apiClient.get(`${API_ENDPOINTS.INVESTASI.LIST}?${investasiParams.toString()}`),
          apiClient.get(`${API_ENDPOINTS.TABUNGAN.LIST}?${tabunganParams.toString()}`)
        ]);

        // Handle response investasi
        // Struktur response: { status: 200, message: "...", data: { investasis: [], pagination: {...} } }
        let investasiData = [];
        if (investasiResponse) {
          // Cek jika response langsung memiliki struktur { status, message, data }
          if (investasiResponse.data && investasiResponse.data.investasis) {
            investasiData = Array.isArray(investasiResponse.data.investasis) 
              ? investasiResponse.data.investasis 
              : [];
          } 
          // Fallback jika struktur berbeda
          else if (Array.isArray(investasiResponse.investasis)) {
            investasiData = investasiResponse.investasis;
          } else if (Array.isArray(investasiResponse)) {
            investasiData = investasiResponse;
          }
        }

        // Handle response tabungan
        // Struktur response: { status: 200, message: "...", data: { tabungans: [], pagination: {...} } }
        let tabunganData = [];
        if (tabunganResponse) {
          // Cek jika response langsung memiliki struktur { status, message, data }
          if (tabunganResponse.data && tabunganResponse.data.tabungans) {
            tabunganData = Array.isArray(tabunganResponse.data.tabungans) 
              ? tabunganResponse.data.tabungans 
              : [];
          } 
          // Fallback jika struktur berbeda
          else if (Array.isArray(tabunganResponse.tabungans)) {
            tabunganData = tabunganResponse.tabungans;
          } else if (Array.isArray(tabunganResponse)) {
            tabunganData = tabunganResponse;
          }
        }

        // Filter tabungan aktif (yang belum deleted)
        const tabunganAktif = tabunganData.filter(item => {
          // Tabungan aktif jika deleted_at adalah null
          return item.deleted_at === null;
        });

        setStats({
          totalInvestasi: investasiData.length,
          totalTabunganAktif: tabunganAktif.length,
          loading: false
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
        setStats({
          totalInvestasi: 0,
          totalTabunganAktif: 0,
          loading: false
        });
      }
    };

    if (userData.id) {
      fetchStats();
    }
  }, [userData.id]);
  const handleLogout = async () => {
    if (!window.confirm('Apakah Anda yakin ingin keluar?')) {
      return;
    }

    try {
      // Hit API logout
      await apiClient.post(API_ENDPOINTS.AUTH.LOGOUT);
    } catch (error) {
      console.error('Error calling logout API:', error);
      // Tetap lanjutkan proses logout meskipun API error
    } finally {
      // Hapus semua data dari localStorage
      localStorage.removeItem('auth_token');
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user_data');
      
      // Remove token dari apiClient
      apiClient.removeAuthToken();
      
      // Redirect ke halaman login
      window.location.href = '/login';
    }
  };

  // Handler untuk toggle edit mode
  const handleEditProfile = () => {
    setIsEditMode(true);
    setEditFormData({
      full_name: userData.full_name || '',
      email: userData.email || '',
      phone_no: userData.phone_no || '',
      alamat: userData.alamat || '',
      birth_date: userData.birth_date ? userData.birth_date.split('T')[0] : '' // Format YYYY-MM-DD untuk input date
    });
    setErrors({});
  };

  // Handler untuk cancel edit
  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditFormData({
      full_name: '',
      email: '',
      phone_no: '',
      alamat: '',
      birth_date: ''
    });
    setErrors({});
  };

  // Handler untuk save profil
  const handleSaveProfile = async () => {
    try {
      setIsSaving(true);
      setErrors({});

      if (!userData.id) {
        alert('User ID tidak ditemukan');
        return;
      }

      // Validasi
      if (!editFormData.full_name || editFormData.full_name.trim() === '') {
        setErrors({ full_name: 'Nama lengkap wajib diisi' });
        setIsSaving(false);
        return;
      }

      // Prepare data untuk API (hanya kirim field yang diisi)
      const updateData = {
        full_name: editFormData.full_name.trim(),
      };

      if (editFormData.email && editFormData.email.trim() !== '') {
        updateData.email = editFormData.email.trim();
      }

      if (editFormData.phone_no && editFormData.phone_no.trim() !== '') {
        updateData.phone_no = editFormData.phone_no.trim();
      }

      if (editFormData.alamat && editFormData.alamat.trim() !== '') {
        updateData.alamat = editFormData.alamat.trim();
      }

      if (editFormData.birth_date && editFormData.birth_date.trim() !== '') {
        updateData.birth_date = editFormData.birth_date.trim();
      }

      // Update ke API
      const response = await apiClient.put(API_ENDPOINTS.USER.UPDATE_BY_ID(userData.id), updateData);

      // Handle response
      let updatedUser = null;
      if (response.data && response.data.user) {
        updatedUser = response.data.user;
      } else if (response.data && response.data.data) {
        updatedUser = response.data.data;
      } else if (response.data && !response.data.user) {
        updatedUser = response.data;
      } else if (!response.data) {
        updatedUser = response;
      }

      if (updatedUser) {
        // Update state userData
        setUserData({
          full_name: updatedUser.full_name || editFormData.full_name,
          email: updatedUser.email || editFormData.email,
          phone_no: updatedUser.phone_no || editFormData.phone_no,
          alamat: updatedUser.alamat || editFormData.alamat,
          birth_date: updatedUser.birth_date || editFormData.birth_date,
          id: updatedUser.id || userData.id
        });

        // Update localStorage jika ada
        const savedUserData = localStorage.getItem('user_data');
        if (savedUserData) {
          const parsed = JSON.parse(savedUserData);
          const updatedLocalStorage = {
            ...parsed,
            full_name: updatedUser.full_name || editFormData.full_name,
            email: updatedUser.email || editFormData.email
          };
          localStorage.setItem('user_data', JSON.stringify(updatedLocalStorage));
        }

        setIsEditMode(false);
        alert('Profil berhasil diupdate');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      if (error.status === 422) {
        const validationErrors = error.data?.detail || [];
        if (validationErrors.length > 0) {
          const errorMessages = validationErrors.map(err => err.msg).join(', ');
          alert(`Validasi error: ${errorMessages}`);
        } else {
          alert(error.data?.message || 'Data tidak valid');
        }
      } else {
        alert(error.message || 'Gagal mengupdate profil. Silakan coba lagi.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Handler untuk open password form
  const handleChangePassword = () => {
    setShowPasswordForm(true);
    setPasswordFormData({
      current_password: '',
      new_password: '',
      confirm_password: ''
    });
    setErrors({});
  };

  // Handler untuk close password form
  const handleClosePasswordForm = () => {
    setShowPasswordForm(false);
    setPasswordFormData({
      current_password: '',
      new_password: '',
      confirm_password: ''
    });
    setErrors({});
  };

  // Handler untuk save password
  const handleSavePassword = async () => {
    try {
      setIsChangingPassword(true);
      setErrors({});

      // Validasi
      if (!passwordFormData.current_password) {
        setErrors({ current_password: 'Password lama wajib diisi' });
        setIsChangingPassword(false);
        return;
      }

      if (!passwordFormData.new_password || passwordFormData.new_password.length < 6) {
        setErrors({ new_password: 'Password baru minimal 6 karakter' });
        setIsChangingPassword(false);
        return;
      }

      if (passwordFormData.new_password !== passwordFormData.confirm_password) {
        setErrors({ confirm_password: 'Konfirmasi password tidak sama' });
        setIsChangingPassword(false);
        return;
      }

      if (!userData.id) {
        alert('User ID tidak ditemukan');
        setIsChangingPassword(false);
        return;
      }

      // Update password ke API
      const response = await apiClient.put(API_ENDPOINTS.USER.UPDATE_PASSWORD(userData.id), {
        current_password: passwordFormData.current_password,
        new_password: passwordFormData.new_password
      });

      alert('Password berhasil diubah');
      handleClosePasswordForm();
    } catch (error) {
      console.error('Error changing password:', error);
      if (error.status === 400 || error.status === 401) {
        alert(error.data?.message || 'Password lama salah');
      } else if (error.status === 422) {
        const validationErrors = error.data?.detail || [];
        if (validationErrors.length > 0) {
          const errorMessages = validationErrors.map(err => err.msg).join(', ');
          alert(`Validasi error: ${errorMessages}`);
        } else {
          alert(error.data?.message || 'Data tidak valid');
        }
      } else {
        alert(error.message || 'Gagal mengubah password. Silakan coba lagi.');
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  // Jika sedang menampilkan halaman keamanan
  if (showSecurityPage) {
    return (
      <SecurityPage onBack={() => setShowSecurityPage(false)} />
    );
  }

  return (
    <section id="account-page" className="min-h-screen bg-white">
      {/* Mobile Wrapper */}
      <div className="max-w-lg mx-auto bg-white shadow-2xl min-h-screen">
        
        {/* Header */}
        <header className="px-4 pt-8 pb-4 bg-white">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <button 
                onClick={onBack}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <i className="fa-solid fa-arrow-left text-primary text-xl"></i>
              </button>
              <h1 className="text-xl font-bold text-primary">Akun Saya</h1>
            </div>
            <button 
              onClick={handleLogout}
              className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center space-x-2"
            >
              <i className="fa-solid fa-sign-out-alt"></i>
              <span>Keluar</span>
            </button>
          </div>
        </header>

        {/* Main Content */}
        <main className="pb-24 bg-gray-50">
          {/* Profile Section */}
          <section className="px-4 pt-4">
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-20 h-20 rounded-full border-4 border-accent bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                  <i className="fa-solid fa-user text-primary text-3xl"></i>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-primary">{userData.full_name}</h2>
                  <p className="text-sm text-gray-500">{userData.email || 'Email tidak tersedia'}</p>
                  <p className="text-sm text-gray-500 mt-1">
                    {userData.birth_date 
                      ? `Bergabung sejak ${new Date(userData.birth_date).toLocaleDateString('id-ID', { year: 'numeric', month: 'long' })}` 
                      : 'Bergabung sejak Januari 2024'}
                  </p>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-primary">
                      {stats.loading ? '...' : stats.totalInvestasi}
                    </p>
                    <p className="text-sm text-gray-500">Total Investasi</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-primary">
                      {stats.loading ? '...' : stats.totalTabunganAktif}
                    </p>
                    <p className="text-sm text-gray-500">Tabungan Aktif</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Account Details */}
          <section className="px-4 mt-6">
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-primary">Informasi Akun</h3>
                {isEditMode && (
                  <div className="flex space-x-2">
                    <button
                      onClick={handleCancelEdit}
                      className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                    >
                      Batal
                    </button>
                    <button
                      onClick={handleSaveProfile}
                      disabled={isSaving}
                      className="px-4 py-1 text-sm bg-primary text-white rounded-lg hover:bg-secondary transition-colors disabled:opacity-50"
                    >
                      {isSaving ? 'Menyimpan...' : 'Simpan'}
                    </button>
                  </div>
                )}
              </div>
              
              {isEditMode ? (
                <div className="space-y-4">
                  <div className="py-3 border-b border-gray-100">
                    <label className="block font-semibold text-gray-800 mb-2">Nama Lengkap</label>
                    <input
                      type="text"
                      value={editFormData.full_name}
                      onChange={(e) => setEditFormData({ ...editFormData, full_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="Nama lengkap"
                    />
                    {errors.full_name && <p className="text-red-500 text-xs mt-1">{errors.full_name}</p>}
                  </div>
                  
                  <div className="py-3 border-b border-gray-100">
                    <label className="block font-semibold text-gray-800 mb-2">Email</label>
                    <input
                      type="email"
                      value={editFormData.email}
                      onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="Email"
                    />
                  </div>
                  
                  <div className="py-3 border-b border-gray-100">
                    <label className="block font-semibold text-gray-800 mb-2">Nomor Telepon</label>
                    <input
                      type="text"
                      value={editFormData.phone_no}
                      onChange={(e) => setEditFormData({ ...editFormData, phone_no: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="Nomor telepon"
                    />
                  </div>
                  
                  <div className="py-3 border-b border-gray-100">
                    <label className="block font-semibold text-gray-800 mb-2">Alamat</label>
                    <textarea
                      value={editFormData.alamat}
                      onChange={(e) => setEditFormData({ ...editFormData, alamat: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="Alamat"
                      rows="3"
                    />
                  </div>
                  
                  <div className="py-3">
                    <label className="block font-semibold text-gray-800 mb-2">Tanggal Lahir</label>
                    <input
                      type="date"
                      value={editFormData.birth_date}
                      onChange={(e) => setEditFormData({ ...editFormData, birth_date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>
              ) : (
              <div className="space-y-4">
                  <div className="py-3 border-b border-gray-100">
                    <div>
                      <p className="font-semibold text-gray-800">Nama Lengkap</p>
                      <p className="text-sm text-gray-500">{userData.full_name || '-'}</p>
                    </div>
                  </div>
                  
                  <div className="py-3 border-b border-gray-100">
                  <div>
                    <p className="font-semibold text-gray-800">Email</p>
                      <p className="text-sm text-gray-500">{userData.email || '-'}</p>
                  </div>
                </div>
                
                  <div className="py-3 border-b border-gray-100">
                  <div>
                    <p className="font-semibold text-gray-800">Nomor Telepon</p>
                      <p className="text-sm text-gray-500">{userData.phone_no || '-'}</p>
                  </div>
                </div>
                
                  <div className="py-3 border-b border-gray-100">
                  <div>
                    <p className="font-semibold text-gray-800">Alamat</p>
                      <p className="text-sm text-gray-500">{userData.alamat || '-'}</p>
                  </div>
                </div>
                
                  <div className="py-3 border-b border-gray-100">
                  <div>
                    <p className="font-semibold text-gray-800">Tanggal Lahir</p>
                      <p className="text-sm text-gray-500">
                        {userData.birth_date 
                          ? new Date(userData.birth_date).toLocaleDateString('id-ID', { 
                              day: 'numeric', 
                              month: 'long', 
                              year: 'numeric' 
                            })
                          : '-'}
                      </p>
                  </div>
                </div>
                
                  <div className="py-3">
                  <div>
                    <p className="font-semibold text-gray-800">Status Verifikasi</p>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className="bg-green-100 text-green-600 text-xs font-semibold px-2 py-1 rounded-full">
                        Terverifikasi
                      </span>
                      <i className="fa-solid fa-check-circle text-green-500"></i>
                    </div>
                  </div>
                </div>
              </div>
              )}
            </div>
          </section>

          {/* Settings Menu */}
          <section className="px-4 mt-6">
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-lg font-bold text-primary mb-4">Pengaturan</h3>
              
              <div className="space-y-2">
                <button 
                  onClick={handleEditProfile}
                  disabled={isEditMode}
                  className="w-full flex justify-between items-center py-3 px-4 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  <div className="flex items-center space-x-3">
                    <i className="fa-solid fa-user-edit text-primary"></i>
                    <span className="font-medium text-gray-800">Edit Profil</span>
                  </div>
                  <i className="fa-solid fa-chevron-right text-gray-400"></i>
                </button>
                
                <button 
                  onClick={handleChangePassword}
                  className="w-full flex justify-between items-center py-3 px-4 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <i className="fa-solid fa-lock text-primary"></i>
                    <span className="font-medium text-gray-800">Ubah Password</span>
                  </div>
                  <i className="fa-solid fa-chevron-right text-gray-400"></i>
                </button>
                
                <button 
                  onClick={() => setShowSecurityPage(true)}
                  className="w-full flex justify-between items-center py-3 px-4 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <i className="fa-solid fa-shield-alt text-primary"></i>
                    <span className="font-medium text-gray-800">Keamanan</span>
                  </div>
                  <i className="fa-solid fa-chevron-right text-gray-400"></i>
                </button>
                
                <button 
                  onClick={() => {
                    window.open('/kontak', '_blank');
                  }}
                  className="w-full flex justify-between items-center py-3 px-4 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <i className="fa-solid fa-question-circle text-primary"></i>
                    <span className="font-medium text-gray-800">Bantuan</span>
                  </div>
                  <i className="fa-solid fa-chevron-right text-gray-400"></i>
                </button>
              </div>
            </div>
          </section>
        </main>

        {/* Password Change Modal */}
        {showPasswordForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-primary">Ubah Password</h3>
                <button
                  onClick={handleClosePasswordForm}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <i className="fa-solid fa-times text-xl"></i>
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block font-semibold text-gray-800 mb-2">Password Lama</label>
                  <input
                    type="password"
                    value={passwordFormData.current_password}
                    onChange={(e) => setPasswordFormData({ ...passwordFormData, current_password: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Masukkan password lama"
                  />
                  {errors.current_password && <p className="text-red-500 text-xs mt-1">{errors.current_password}</p>}
                </div>
                
                <div>
                  <label className="block font-semibold text-gray-800 mb-2">Password Baru</label>
                  <input
                    type="password"
                    value={passwordFormData.new_password}
                    onChange={(e) => setPasswordFormData({ ...passwordFormData, new_password: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Masukkan password baru (min 6 karakter)"
                  />
                  {errors.new_password && <p className="text-red-500 text-xs mt-1">{errors.new_password}</p>}
                </div>
                
                <div>
                  <label className="block font-semibold text-gray-800 mb-2">Konfirmasi Password Baru</label>
                  <input
                    type="password"
                    value={passwordFormData.confirm_password}
                    onChange={(e) => setPasswordFormData({ ...passwordFormData, confirm_password: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Konfirmasi password baru"
                  />
                  {errors.confirm_password && <p className="text-red-500 text-xs mt-1">{errors.confirm_password}</p>}
                </div>
                
                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={handleClosePasswordForm}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Batal
                  </button>
                  <button
                    onClick={handleSavePassword}
                    disabled={isChangingPassword}
                    className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-secondary transition-colors disabled:opacity-50"
                  >
                    {isChangingPassword ? 'Mengubah...' : 'Simpan'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bottom Navigation */}
        <footer className="fixed bottom-0 left-0 right-0 max-w-lg mx-auto bg-white pt-2">
          <div className="bg-primary mx-4 mb-4 rounded-2xl shadow-lg">
            <div className="flex justify-around items-center h-20 text-white">
              <span 
                className={`text-center flex flex-col items-center cursor-pointer ${activeBottomTab === 'home' ? 'text-accent' : 'opacity-70 hover:opacity-100'} transition`}
                onClick={() => {
                  setActiveBottomTab('home');
                  onBack();
                }}
              >
                <i className="fa-solid fa-house text-2xl"></i>
                <span className="text-xs font-semibold mt-1">Beranda</span>
              </span>
              <span 
                className={`text-center flex flex-col items-center cursor-pointer ${activeBottomTab === 'savings' ? 'text-accent' : 'opacity-70 hover:opacity-100'} transition`}
                onClick={() => setActiveBottomTab('savings')}
              >
                <i className="fa-solid fa-piggy-bank text-2xl"></i>
                <span className="text-xs font-semibold mt-1">Tabungan</span>
              </span>
              <span 
                className={`text-center flex flex-col items-center cursor-pointer ${activeBottomTab === 'products' ? 'text-accent' : 'opacity-70 hover:opacity-100'} transition`}
                onClick={() => setActiveBottomTab('products')}
              >
                <i className="fa-solid fa-box-archive text-2xl"></i>
                <span className="text-xs font-semibold mt-1">Produk</span>
              </span>
              <span 
                className={`text-center flex flex-col items-center cursor-pointer ${activeBottomTab === 'investment' ? 'text-accent' : 'opacity-70 hover:opacity-100'} transition`}
                onClick={() => setActiveBottomTab('investment')}
              >
                <i className="fa-solid fa-arrow-trend-up text-2xl"></i>
                <span className="text-xs font-semibold mt-1">Investasi</span>
              </span>
              <span 
                className={`text-center flex flex-col items-center cursor-pointer ${activeBottomTab === 'account' ? 'text-accent' : 'opacity-70 hover:opacity-100'} transition`}
                onClick={() => setActiveBottomTab('account')}
              >
                <i className="fa-solid fa-user-circle text-2xl"></i>
                <span className="text-xs font-semibold mt-1">Akun</span>
              </span>
            </div>
          </div>
        </footer>
      </div>
    </section>
  );
};

export default AccountPage;
